/**
 * The SQL of the pending ("orphan") reports that read the analytics database (D527; doc 20 section 7): the company's request report on the
 * partner desk (E-049), the driver km report (I-013 / D-056), the customers / companies / requests-with-creator lists (I-052), the
 * quote-simulation report with its route provider (C-045) and the ads report (H-037). Pure: parameterized text and the row each statement
 * returns; the API runs it in ONE read-only transaction as the least-privilege reader and folds the rows into the wire.
 *
 * Same discipline as `panels.ts`: every statement that carries a tenant lives here, the tenant of a partner report is a
 * {@link PartnerAccountTenant} built by the server, and its ids and markets are bound parameters (`$1`, `$2`). Two audiences:
 *
 *  - PARTNER (E-049 only): the desk's own account. No guest name or phone exists in the store; the creating operator crosses as the PSEUDONYMOUS
 *    operator key (the API turns it into a stable "agent n" label and never sends the key); nothing of a person is selected.
 *  - INTERNAL (the Admin): statements that never select a personal COLUMN (name, phone, plate, model) run as `analytics_reader`; the personal columns
 *    live in SEPARATE statements ({@link driverPeopleQuery}, {@link customerPeopleQuery}) that the API runs only for a caller who holds
 *    `analytics:view_personal`, under the personal reader role, and logs.
 *
 * Periods are LOCAL days: the Admin lists cut on `date_key` (the market's own local date, exactly), the desk cuts with `AT TIME ZONE` the account's
 * billing zone (a desk account may operate in markets of several zones; its statements close in one).
 */
import { SEMANTIC_VOCABULARY } from './catalog.js';
const K = SEMANTIC_VOCABULARY.sourceKind;
export const ORPHAN_LIMITS = {
    /** Rows of one page of any list. */
    maxPage: 500,
    /** Rows of a preview / default page. */
    defaultPage: 100,
    /** Rows of a synchronous CSV export. */
    exportRows: 20_000,
    /** The deepest offset a page may ask for. */
    maxOffset: 20_000,
    /** D594: the deepest offset a BACKGROUND export of a list may read (`AdminListScope.deep`): five pages of `exportRows`, one repeatable-read snapshot. */
    deepMaxOffset: 80_000,
    /** D594: the most rows a background export of a list carries (`deepMaxOffset` + one page). */
    deepExportRows: 100_000,
    /** Values of one multi-value filter. */
    maxValues: 50,
};
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const date = (v) => {
    if (!DATE.test(v))
        throw new RangeError('a report date is YYYY-MM-DD');
    return v;
};
const clampInt = (v, lo, hi) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.trunc(v))) : lo);
const nullable = (values) => (values === null || values === undefined || values.length === 0 ? null : [...values]);
/** The charge of an order: the live postings (authorized or settled; a voided one is spend 0) of the SAME order, near its day. */
const CHARGE = `LEFT JOIN LATERAL (
    SELECT sum(m.amount_minor) AS amount, min(m.currency::text) AS currency
      FROM analytics.fact_money m
     WHERE m.market_id = f.market_id AND m.order_id = f.order_id AND m.vertical = f.vertical
       AND m.source_kind IN ('${K.partnerChargeAuthorized}', '${K.partnerChargeSettled}')
       AND m.date_key BETWEEN f.date_key - 2 AND f.date_key + 31) pay ON true`;
const ACCOUNT = `JOIN analytics.dim_partner_account pa ON pa.market_id = f.market_id AND pa.partner_account_key = f.partner_account_key AND pa.natural_key = $2`;
const requestScope = () => `f.market_id = ANY($1::text[]) AND f.date_key BETWEEN $3::date - 2 AND $4::date + 2
   AND (f.requested_at AT TIME ZONE $5::text)::date BETWEEN $3::date AND $4::date
   AND ($6::text[] IS NULL OR f.vertical = ANY($6::text[]))
   AND ($7::text[] IS NULL OR f.created_by_operator_key = ANY($7::text[]))`;
function requestParams(tenant, f) {
    return [[...tenant.marketIds], tenant.accountRef, date(f.from), date(f.to), f.timeZone, nullable(f.kinds), nullable(f.operatorKeys)];
}
/**
 * The groups behind the charts, the totals and the status filter, plus the ordinal source of the agent labels. `$1` = the account's markets, `$2`
 * = the account; the filters are `$6` (kinds) and `$7` (operator keys). The status filter is applied by {@link partnerRequestRows}.
 */
export function partnerRequestsQueries(tenant, f) {
    const params = requestParams(tenant, f);
    return {
        markets: tenant.marketIds,
        groups: {
            sql: `SELECT ((f.requested_at AT TIME ZONE $5::text)::date)::text AS day, f.vertical, f.status, pay.currency, count(*)::int AS n, COALESCE(sum(pay.amount), 0)::text AS amount
  FROM analytics.fact_order f ${ACCOUNT} ${CHARGE}
 WHERE ${requestScope()}
 GROUP BY 1, 2, 3, 4`,
            params,
        },
        // The labels "agent 1..n" are the order of first appearance over the account's WHOLE history, in EVERY market of the scope (the route passes the
        // markets the account ever operated), with no cap: a LOOSE INDEX SCAN on `fact_order_partner_agent` hops from one operator key to the next, so the cost is
        // (keys x log) and not the account's order count. Ties on the instant are broken by the key: a total, stable order.
        agents: {
            sql: `WITH RECURSIVE acct AS (
  SELECT pa.market_id, pa.partner_account_key FROM analytics.dim_partner_account pa
   WHERE pa.market_id = ANY($1::text[]) AND pa.natural_key = $2
), walk AS (
  SELECT a.market_id, a.partner_account_key,
         (SELECT min(f.created_by_operator_key) FROM analytics.fact_order f
           WHERE f.market_id = a.market_id AND f.partner_account_key = a.partner_account_key AND f.created_by_operator_key IS NOT NULL) AS operator_key
    FROM acct a
  UNION ALL
  SELECT w.market_id, w.partner_account_key,
         (SELECT min(f.created_by_operator_key) FROM analytics.fact_order f
           WHERE f.market_id = w.market_id AND f.partner_account_key = w.partner_account_key AND f.created_by_operator_key > w.operator_key)
    FROM walk w WHERE w.operator_key IS NOT NULL
)
SELECT w.operator_key, min(first.requested_at) AS first_seen
  FROM walk w
 CROSS JOIN LATERAL (SELECT min(f.requested_at) AS requested_at FROM analytics.fact_order f
                      WHERE f.market_id = w.market_id AND f.partner_account_key = w.partner_account_key AND f.created_by_operator_key = w.operator_key) first
 WHERE w.operator_key IS NOT NULL
 GROUP BY 1 ORDER BY min(first.requested_at), w.operator_key`,
            params: [[...tenant.marketIds], tenant.accountRef],
        },
    };
}
/** One page of the account's requests, newest first. Same filters as the groups, plus the resolved statuses. */
export function partnerRequestRows(tenant, f, page) {
    const limit = clampInt(page.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(page.offset, 0, ORPHAN_LIMITS.maxOffset);
    return {
        sql: `SELECT f.requested_at, f.vertical, f.order_id, f.order_code, f.status, f.outcome, f.category_code, f.created_by_operator_key AS operator_key,
       pay.amount::text AS amount, COALESCE(pay.currency, f.currency::text) AS currency, mk.currency::text AS market_currency
  FROM analytics.fact_order f ${ACCOUNT}
  JOIN analytics.dim_market mk ON mk.market_id = f.market_id ${CHARGE}
 WHERE ${requestScope()} AND ($8::text[] IS NULL OR (f.vertical || '|' || f.status) = ANY($8::text[]))
 ORDER BY f.requested_at DESC, f.order_id DESC
 LIMIT $9 OFFSET $10`,
        params: [...requestParams(tenant, f), nullable(page.kindStatus), limit, offset],
    };
}
/** The deepest offset this list read may use. */
const offsetCap = (f) => (f.deep === true ? ORPHAN_LIMITS.deepMaxOffset : ORPHAN_LIMITS.maxOffset);
export function driverKmQuery(f) {
    const limit = clampInt(f.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(f.offset, 0, offsetCap(f));
    const minMeters = f.minMeters === null || f.minMeters === undefined || !(f.minMeters > 0) ? 0 : Math.trunc(f.minMeters);
    return {
        sql: `WITH g AS (
  SELECT d.market_id, d.pseudonymous_key, f.currency::text AS currency,
         count(*)::int AS requests,
         (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
         COALESCE(sum(f.distance_meters) FILTER (WHERE f.status = 'COMPLETED'), 0)::bigint AS distance_meters,
         COALESCE(sum(COALESCE(f.final_minor, f.quoted_minor)) FILTER (WHERE f.status = 'COMPLETED'), 0)::text AS fares_minor,
         COALESCE(sum(m.gross_minor), 0)::text AS cash_due_minor,
         COALESCE(sum(m.amount_minor), 0)::text AS cash_collected_minor,
         COALESCE(sum(m.commission_minor), 0)::text AS cash_commission_minor,
         (count(m.source_id))::int AS cash_rides
    FROM analytics.fact_order f
    JOIN analytics.dim_driver d ON d.market_id = f.market_id AND d.driver_key = f.driver_key
    LEFT JOIN analytics.fact_money m ON m.market_id = f.market_id AND m.order_id = f.order_id AND m.source_kind = '${K.rideCash}' AND m.vertical = 'RIDE'
                                   AND m.date_key BETWEEN f.date_key - 1 AND f.date_key + 31 AND f.status = 'COMPLETED'
   WHERE f.market_id = ANY($1::text[]) AND f.vertical = 'RIDE' AND f.driver_key IS NOT NULL AND f.date_key BETWEEN $2::date AND $3::date
     AND ($4::text[] IS NULL OR d.pseudonymous_key = ANY($4::text[]))
   GROUP BY 1, 2, 3),
 h AS (SELECT g.*, count(*) OVER ()::int AS total FROM g WHERE g.distance_meters >= $5::bigint)
SELECT * FROM h ORDER BY h.distance_meters DESC, h.market_id, h.pseudonymous_key, h.currency NULLS LAST LIMIT $6 OFFSET $7`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.driverKeys), minMeters, limit, offset],
    };
}
export function driverPeopleQuery(markets, driverKeys) {
    return {
        sql: `SELECT d.market_id, d.pseudonymous_key, d.display_name, d.vehicle_plate, d.vehicle_model
  FROM analytics.dim_driver d
 WHERE d.market_id = ANY($1::text[]) AND d.is_current AND d.pseudonymous_key = ANY($2::text[])`,
        params: [[...markets], [...driverKeys]],
    };
}
export function driverKmMarketsQuery(f) {
    const minMeters = f.minMeters === null || f.minMeters === undefined || !(f.minMeters > 0) ? 0 : Math.trunc(f.minMeters);
    return {
        sql: `WITH g AS (
  SELECT d.market_id, d.pseudonymous_key AS driver_key, f.currency::text AS currency, count(*)::int AS requests,
         (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
         COALESCE(sum(f.distance_meters) FILTER (WHERE f.status = 'COMPLETED'), 0)::bigint AS distance_meters,
         COALESCE(sum(COALESCE(f.final_minor, f.quoted_minor)) FILTER (WHERE f.status = 'COMPLETED'), 0)::bigint AS fares_minor,
         COALESCE(sum(m.amount_minor), 0)::bigint AS cash_collected_minor
    FROM analytics.fact_order f
    JOIN analytics.dim_driver d ON d.market_id = f.market_id AND d.driver_key = f.driver_key
    LEFT JOIN analytics.fact_money m ON m.market_id = f.market_id AND m.order_id = f.order_id AND m.source_kind = '${K.rideCash}' AND m.vertical = 'RIDE'
                                   AND m.date_key BETWEEN f.date_key - 1 AND f.date_key + 31 AND f.status = 'COMPLETED'
   WHERE f.market_id = ANY($1::text[]) AND f.vertical = 'RIDE' AND f.driver_key IS NOT NULL AND f.date_key BETWEEN $2::date AND $3::date
     AND ($4::text[] IS NULL OR d.pseudonymous_key = ANY($4::text[]))
   GROUP BY 1, 2, 3)
SELECT market_id, currency, count(DISTINCT driver_key)::int AS drivers, sum(requests)::int AS requests, sum(completed)::int AS completed,
       sum(distance_meters)::text AS distance_meters, sum(fares_minor)::text AS fares_minor, sum(cash_collected_minor)::text AS cash_collected_minor
  FROM g WHERE distance_meters >= $5::bigint GROUP BY market_id, currency ORDER BY market_id, currency NULLS LAST`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.driverKeys), minMeters],
    };
}
export function customerListQuery(f) {
    const limit = clampInt(f.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(f.offset, 0, offsetCap(f));
    const minRequests = f.minRequests === null || f.minRequests === undefined || !(f.minRequests > 0) ? 0 : Math.trunc(f.minRequests);
    return {
        sql: `WITH o AS (
  SELECT c.market_id, c.customer_key, c.pseudonymous_key, count(*)::int AS requests,
         (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.completed}'))::int AS completed,
         (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.cancelled}'))::int AS cancelled,
         min(f.requested_at) AS first_at, max(f.requested_at) AS last_at
    FROM analytics.fact_order f
    JOIN analytics.dim_customer c ON c.market_id = f.market_id AND c.customer_key = f.customer_key
   WHERE f.market_id = ANY($1::text[]) AND f.customer_key IS NOT NULL AND f.date_key BETWEEN $2::date AND $3::date
     AND ($4::text[] IS NULL OR f.vertical = ANY($4::text[]))
   GROUP BY 1, 2, 3),
 s AS (
  SELECT m.market_id, m.customer_key, m.currency::text AS currency, sum(m.amount_minor) AS spend_minor
    FROM analytics.fact_money m
   WHERE m.market_id = ANY($1::text[]) AND m.customer_key IS NOT NULL AND m.date_key BETWEEN $2::date AND $3::date
     AND m.source_kind IN ('${K.orderBooking}', '${K.rideCash}', '${K.rideSettlement}')
   GROUP BY 1, 2, 3),
 h AS (
  SELECT o.market_id, o.pseudonymous_key, o.requests, o.completed, o.cancelled, o.first_at, o.last_at, s.currency, COALESCE(s.spend_minor, 0)::text AS spend_minor,
         count(*) OVER ()::int AS total
    FROM o LEFT JOIN s ON s.market_id = o.market_id AND s.customer_key = o.customer_key WHERE o.requests >= $5::int)
SELECT * FROM h ORDER BY h.requests DESC, h.market_id, h.pseudonymous_key, h.currency NULLS LAST LIMIT $6 OFFSET $7`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.kinds), minRequests, limit, offset],
    };
}
/** The personal columns of the customers on a page. Personal reader role only. */
export function customerPeopleQuery(markets, customerKeys) {
    return {
        sql: `SELECT c.market_id, c.pseudonymous_key, c.display_name, c.phone_masked
  FROM analytics.dim_customer c WHERE c.market_id = ANY($1::text[]) AND c.pseudonymous_key = ANY($2::text[])`,
        params: [[...markets], [...customerKeys]],
    };
}
export function companyListQuery(f) {
    const limit = clampInt(f.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(f.offset, 0, offsetCap(f));
    return {
        sql: `WITH o AS (
  SELECT pa.market_id, pa.natural_key AS account_ref, max(pa.display_name) AS display_name, count(*)::int AS requests,
         (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.completed}'))::int AS completed,
         (count(*) FILTER (WHERE f.outcome = '${SEMANTIC_VOCABULARY.outcome.cancelled}'))::int AS cancelled
    FROM analytics.fact_order f
    JOIN analytics.dim_partner_account pa ON pa.market_id = f.market_id AND pa.partner_account_key = f.partner_account_key
   WHERE f.market_id = ANY($1::text[]) AND f.partner_account_key IS NOT NULL AND f.date_key BETWEEN $2::date AND $3::date
   GROUP BY 1, 2),
 s AS (
  SELECT pa.market_id, pa.natural_key AS account_ref, m.currency::text AS currency, sum(m.amount_minor) AS spend_minor
    FROM analytics.fact_money m
    JOIN analytics.dim_partner_account pa ON pa.market_id = m.market_id AND pa.partner_account_key = m.partner_account_key
   WHERE m.market_id = ANY($1::text[]) AND m.date_key BETWEEN $2::date AND $3::date
     AND m.source_kind IN ('${K.partnerChargeAuthorized}', '${K.partnerChargeSettled}')
   GROUP BY 1, 2, 3),
 h AS (
  SELECT o.market_id, o.account_ref, o.display_name, o.requests, o.completed, o.cancelled, s.currency, COALESCE(s.spend_minor, 0)::text AS spend_minor, count(*) OVER ()::int AS total
    FROM o LEFT JOIN s ON s.market_id = o.market_id AND s.account_ref = o.account_ref)
SELECT * FROM h ORDER BY h.requests DESC, h.market_id, h.account_ref, h.currency NULLS LAST LIMIT $4 OFFSET $5`,
        params: [[...f.markets], date(f.from), date(f.to), limit, offset],
    };
}
export function createdRequestsQuery(f) {
    const limit = clampInt(f.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(f.offset, 0, offsetCap(f));
    return {
        sql: `SELECT f.market_id, f.requested_at, f.vertical, f.order_id, f.order_code, f.status, f.channel, f.created_by_operator_key AS operator_key,
       pa.natural_key AS account_ref, pa.display_name AS account_name, count(*) OVER ()::int AS total
  FROM analytics.fact_order f
  LEFT JOIN analytics.dim_partner_account pa ON pa.market_id = f.market_id AND pa.partner_account_key = f.partner_account_key
 WHERE f.market_id = ANY($1::text[]) AND f.created_by_operator_key IS NOT NULL AND f.date_key BETWEEN $2::date AND $3::date
   AND ($4::text[] IS NULL OR f.vertical = ANY($4::text[]))
 ORDER BY f.requested_at DESC, f.order_id DESC LIMIT $5 OFFSET $6`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.kinds), limit, offset],
    };
}
/** The sentinel for a quote that recorded no provider (an old quote). */
export const ROUTE_PROVIDER_UNKNOWN = 'UNKNOWN';
export function quoteProviderQuery(f) {
    const limit = clampInt(f.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(f.offset, 0, offsetCap(f));
    return {
        sql: `SELECT q.market_id, q.quote_id, q.category_code, q.created_at, q.currency::text AS currency, q.total_minor::text AS total_minor, q.distance_meters, q.duration_seconds,
       COALESCE(q.route_provider_id, '${ROUTE_PROVIDER_UNKNOWN}') AS route_provider, q.became_ride, count(*) OVER ()::int AS total
  FROM analytics.fact_quote q
 WHERE q.market_id = ANY($1::text[]) AND q.date_key BETWEEN $2::date AND $3::date
   AND ($4::text[] IS NULL OR COALESCE(q.route_provider_id, '${ROUTE_PROVIDER_UNKNOWN}') = ANY($4::text[]))
   AND ($5::text[] IS NULL OR q.category_code = ANY($5::text[]))
 ORDER BY q.created_at DESC, q.quote_id DESC LIMIT $6 OFFSET $7`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.providers), nullable(f.categories), limit, offset],
    };
}
export function quoteProviderMixQuery(f) {
    return {
        sql: `SELECT q.date_key::text AS day, COALESCE(q.route_provider_id, '${ROUTE_PROVIDER_UNKNOWN}') AS route_provider, count(*)::int AS quotes, (count(*) FILTER (WHERE q.became_ride))::int AS became_ride
  FROM analytics.fact_quote q
 WHERE q.market_id = ANY($1::text[]) AND q.date_key BETWEEN $2::date AND $3::date
   AND ($4::text[] IS NULL OR COALESCE(q.route_provider_id, '${ROUTE_PROVIDER_UNKNOWN}') = ANY($4::text[]))
   AND ($5::text[] IS NULL OR q.category_code = ANY($5::text[]))
 GROUP BY 1, 2 ORDER BY 1, 2`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.providers), nullable(f.categories)],
    };
}
// ───────────────────────────── H-037: the ads report ─────────────────────────────
/** Views and clicks are not captured anywhere yet (the consumer app records none): the report says so with this stable code. */
export const AD_VIEWS_CLICKS_NOT_RECORDED = 'AD_VIEWS_CLICKS_NOT_RECORDED';
export function adListQuery(f) {
    const limit = clampInt(f.limit, 1, ORPHAN_LIMITS.exportRows);
    const offset = clampInt(f.offset, 0, offsetCap(f));
    return {
        sql: `WITH w AS (
  SELECT l.market_id, l.campaign_id, l.merchant_ref, l.date_key, l.currency::text AS currency, l.budget_minor, l.spent_minor, l.charged_exposures, l.converted_exposures,
         l.spent_minor - COALESCE(lag(l.spent_minor) OVER (PARTITION BY l.market_id, l.campaign_id ORDER BY l.date_key), 0) AS spent_day_minor,
         l.charged_exposures - COALESCE(lag(l.charged_exposures) OVER (PARTITION BY l.market_id, l.campaign_id ORDER BY l.date_key), 0) AS charged_day
    FROM analytics.fact_ad_ledger l
   WHERE l.market_id = ANY($1::text[]) AND l.date_key <= $3::date
     AND ($4::text[] IS NULL OR l.campaign_id = ANY($4::text[])) AND ($5::text[] IS NULL OR l.merchant_ref = ANY($5::text[]))),
 h AS (
  SELECT w.market_id, w.campaign_id, w.merchant_ref, (SELECT dm.display_name FROM analytics.dim_merchant dm WHERE dm.market_id = w.market_id AND dm.natural_key = w.merchant_ref AND dm.is_current) AS merchant_name,
         w.date_key::text AS day, w.currency, w.budget_minor::text AS budget_minor, w.spent_minor::text AS spent_minor, (w.budget_minor - w.spent_minor)::text AS balance_minor,
         w.spent_day_minor::text AS spent_day_minor, w.charged_exposures, w.charged_day::int AS charged_day, w.converted_exposures, count(*) OVER ()::int AS total
    FROM w WHERE w.date_key >= $2::date)
SELECT * FROM h ORDER BY h.day DESC, h.market_id, h.campaign_id LIMIT $6 OFFSET $7`,
        params: [[...f.markets], date(f.from), date(f.to), nullable(f.campaigns), nullable(f.merchants), limit, offset],
    };
}
// ───────────────────────────── shared with D534 (the company spend report and the fleet reports) ─────────────────────────────
/** The account join of every partner statement (`$2` = the account the server authorized). Exported for `b2b-fleet.ts`; not a public contract. */
export const PARTNER_ACCOUNT_JOIN = ACCOUNT;
/** The period / kind / operator scope of a partner statement (`$1`, `$3`..`$7`). */
export const partnerRequestScope = requestScope;
/** The order's charge postings (kinds that count as spend), `$`-free: it joins by the fact row `f`. */
export const PARTNER_CHARGE_KINDS = `'${K.partnerChargeAuthorized}', '${K.partnerChargeSettled}'`;
//# sourceMappingURL=orphan-reports.js.map